const getFieldSpecificInstructions = (
  field: string,
  lang: string,
  title: string,
  body?: string,
  publishedAt?: string,
): string => {
  const baseInstructions = `Règles importantes : 1 : Les titres ne doivent jamais dépasser 60 caractères, espace inclus. 2 : respecte la ponctuation de la langue ${lang} : Pas d'espace avant, une espace après : point, virgule, points de suspension, parenthèse fermante, crochet fermant, guillemets anglais fermants. Une espace avant, pas d'espace après : parenthèse ouvrante, crochet ouvrant, guillemets anglais ouvrants. Une espace avant, une espace après : Tiret d'incise. Une espace insécable avant, une espace après : deux-points, point-virgule, point d'exclamation, point d'interrogation, guillemets français fermants, pourcentage, signes mathématiques. Une espace avant, une espace insécable après : guillemets français ouvrants. Mettre une majuscule à chaque début de phrase. Les noms propres prennent toujours une majuscule. Les prénoms, noms et surnoms prennent toujours une majuscule. Les noms de peuples ou d'habitants prennent toujours une majuscule. On ne met pas de majuscule aux adjectifs adjoints aux noms propres géographiques. 3 : Si tu utilises le terme Clickbait dans ta réponse, utilise plutôt le mot "Incitatif". La syntaxe doit-être grammaticalement parfaite et donner envie de lire le contenu`;

  // Only apply field-specific instructions for discoverTitle and partnerTitle
  switch (field) {
    case 'partnerTitle':
      return `Écris en ${lang}. Tu es un journaliste. Créé 3 titres d'accroche qui soient des incitatifs pour attirer l'attention du lecteur. ${baseInstructions} Voici le titre : ${title}`;

    case 'discoverTitle':
      return `Persona :
- Tu es un expert en référencement et en rédaction de titres optimisés pour Google Discover.
- Tu maîtrises les techniques pour maximiser le taux de clics et l'engagement des utilisateurs sans tromper.
- Tu conçois des titres engageants, "putaclic bienveillants" : accrocheurs sans jamais mentir.
- Tu sais t’adapter au type de contenu et à ses spécificités sans qu’on te le dise explicitement.

Objectif :
- Générer 5 titres optimisés Google Discover en fonction du **contenu analysé**.
- Tu dois **déduire le type de contenu automatiquement**, sans instruction explicite de l'utilisateur. Si incertitude, **signale-le** au lieu d’appliquer de mauvaises règles.
- Le style et la structure des titres s’adaptent selon les caractéristiques du contenu détecté (bons plans, comparatifs, recettes, actualités, evergreen…).

Règles communes à tous les contenus :
- Longueur : 50 à 90 caractères (optimisé pour mobile).
- Aucune utilisation d’emojis.
- Pas de texte autour des titres (seulement les titres).
- Vérifie la cohérence entre le titre et le contenu : les mots clés du titre doivent figurer dans l’article.
- Si un titre semble **trop putaclic**, tu dois le **réécrire**.
- Effectue une **vérification interne** après génération : si une règle est violée sur un titre, **regénère tous les titres**.
- Utilise la casse française normale : une seule majuscule au début du titre et aux noms propres.
- Ne mets pas de majuscule à chaque mot. Évite le style "Title Case".

---

# Règles spécifiques par type de contenu (à déduire automatiquement) :

## 1. Bons plans & offres promotionnelles
- Inclure mots-clés comme “promotion”, “remise”, “offre”.
- Chiffres attractifs : “-30%”, “moins de 200€”...
- Verbes incitatifs : “profitez”, “découvrez”...
- Créer un sentiment d’urgence : “durée limitée”, “stock faible”.
- Mettre les marques si présentes (Samsung, iPhone...).
- Émotion positive : “c’est de la folie”, “offre incroyable”.

## 2. Tests, comparatifs, guides produits
- Inclure les mots “test”, “comparatif”, “avis”.
- Organiser avec “:”, “vs”, “-”.
- Mentionner marques et modèles populaires.
- Mettre l’accent sur la différenciation (“le meilleur”, “notre choix”).
- Ajouter une temporalité : “2025”, “nouveau modèle”.
- Utiliser un ton neutre mais vendeur.

## 3. Actualités chaudes (people, télé, immédiat)
- Mettre en avant l’évènement (mariage, décès, accouchement...).
- Ton sérieux et “people”.
- Inclure mystère : question, suspension (“...”).
- Mots forts/superlatifs : “enfin”, “énorme”, “choc”.
- Représenter fidèlement l’ensemble de l’article.

## 4. Recettes de chef
- Citer le nom du chef si présent.
- Mentionner des astuces techniques ou conseils pro.
- Utiliser des superlatifs culinaires : “délicieux”, “incontournable”.
- Ton sérieux mais aussi un peu “people”.

## 5. Recettes de cuisine (hors chef)
- Identifier le plat principal (extrait des entités).
- Ajouter des qualificatifs culinaires (“moelleux”, “express”).
- Mettre en avant avantages clés : “prêt en 15 min”, “seulement 3 ingrédients”.
- Mentionner origine si pertinente : “alsacien”, “façon grand-mère”.

## 6. Actualités de fond (non people)
- Si mono-thématique : forme “[entité principale] : [élément marquant]”.
- Si multi-thématique : énumère les thèmes + “...” à la fin.
- Utiliser une citation centrale si elle structure l’article.
- Ton informatif, précis, sérieux.

## 7. Contenus evergreen (définitions, explications, guides intemporels)
- Clarté absolue sur le sujet principal (scientifique, juridique, santé…).
- Si autorité/source présente, mentionner (“selon le médecin...").
- Utiliser des chiffres si pertinents ("95%", “2000 euros”).
- Ton pédagogique, rassurant.

---

# Résultat attendu : 5 titres optimisés pour Google Discover, respectant les règles ci-dessus.  Trie les du plus pertinent au regard des règles au moins pertinent.

# Format de sortie: un tableau JSON strict sous la forme :
\`\`\`json
{
  "titles": [
    "titre1",
    "titre2"
  ]
}
\`\`\`

# Entrée attendue :

Date de publication : ${publishedAt}

Voici le contenu de l'article :  
"""
${body}
"""
`;

    default:
      // Use the original generic prompt for all other fields
      return `Écris en ${lang}. Tu es un journaliste. Créé 3 titres qui soient des incitatifs. ${baseInstructions} Voici le titre : ${title}`;
  }
};

export const GENERATE_PROMPT = (
  lang: string,
  title: string,
  field = 'partnerTitle',
  body?: string,
  publishedAt?: string,
) => ({
  label: 'Générer',
  body: prependJsonFormatInstructions(
    getFieldSpecificInstructions(field, lang, title, body, publishedAt),
  ),
  field,
});

export const PARAPHRASE_PROMPT = (lang: string, title: string) => ({
  label: 'Paraphraser',
  body: prependJsonFormatInstructions(
    `Écris en ${lang}. Tu es un journaliste. Créé 3 versions paraphrasées du titre en conservant la même longueur et le style que le titre d'origine. Règles importantes : 1 : Les titres ne doivent jamais dépasser 60 caractères, espace inclus. 2 : respecte la ponctuation de la langue ${lang} : Pas d'espace avant, une espace après : point, virgule, points de suspension, parenthèse fermante, crochet fermant, guillemets anglais fermants. Une espace avant, pas d'espace après : parenthèse ouvrante, crochet ouvrant, guillemets anglais ouvrants. Une espace avant, une espace après : Tiret d'incise. Une espace insécable avant, une espace après : deux-points, point-virgule, point d'exclamation, point d'interrogation, guillemets français fermants, pourcentage, signes mathématiques. Une espace avant, une espace insécable après : guillemets français ouvrants. Mettre une majuscule à chaque début de phrase. Les noms propres prennent toujours une majuscule. Les prénoms, noms et surnoms prennent toujours une majuscule. Les noms de peuples ou d'habitants prennent toujours une majuscule. On ne met pas de majuscule aux adjectifs adjoints aux noms propres géographiques. 3 : Si tu utilises le terme Clickbait dans ta réponse, utilise plutôt le mot "Incitatif". La syntaxe doit-être grammaticalement parfaite et donner envie de lire le contenu Voici le titre : ${title}`,
  ),
  field: 'partnerTitle',
  retry: true,
});

/**
 * Prepends JSON format instructions to any prompt for better caching
 */
export const prependJsonFormatInstructions = (prompt: string): string => {
  const jsonFormatInstructions = `# Format de sortie: un tableau JSON strict sous la forme :
\`\`\`json
{
  "titles": [
    "titre1",
    "titre2"
  ]
}
\`\`\`

`;

  return jsonFormatInstructions + prompt;
};

export enum BRAND_KEY_LANGUAGE {
  DE = 'allemand',
  EN = 'anglais',
  ES = 'espagnol',
  FR = 'français',
  IT = 'italien',
  PT = 'portugais',
}
